import React, { useState, useEffect } from "react";
import { Grid, Card, CardActionArea, CardContent, Typography, Box, Button, Dialog, DialogTitle, DialogContent, TextField, DialogActions, IconButton, Menu, MenuItem, Checkbox, FormControlLabel, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StarIcon from '@mui/icons-material/Star';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import API from '../../api2';
import { DATA_CATEGORIES } from '../DataCategories';

interface PresetDataProps {
    setSelectedMetrics: (metrics: { agristarMetrics: string[]; serviceMetrics: string[] }) => void;
    selectedMetrics?: { agristarMetrics: string[]; serviceMetrics: string[] };
}

interface Preset {
    id?: number;
    name: string;
    description?: string;
    icon?: React.ReactNode;
    metrics: {
        agristarMetrics: string[];
        serviceMetrics: string[];
    };
    isCustom?: boolean;
}

function PresetData({ setSelectedMetrics, selectedMetrics }: PresetDataProps) {
    const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
    const [customPresets, setCustomPresets] = useState<Preset[]>([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [newPresetName, setNewPresetName] = useState("");
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [menuPresetId, setMenuPresetId] = useState<number | null>(null);
    const [editingPresetId, setEditingPresetId] = useState<number | null>(null);
    const [dialogMetrics, setDialogMetrics] = useState<{ agristarMetrics: string[]; serviceMetrics: string[] }>({ agristarMetrics: [], serviceMetrics: [] });

    const serviceMetrics = ["sheets",
        "inspections",
        "chemAppAgreement",
        "disinfectantAgreement",
        "sprayBarAgreement",
        "freshPack",
        "refrigeration",
        "preseason",
        "labData",]

    const defaultPresets: Preset[] = [
        {
            name: "Standard Environmental",
            description: "Key metrics for monitoring storage environment conditions like temperature, humidity, and CO2.",
            icon: <ThermostatIcon />,
            metrics: {
                agristarMetrics: ["alarms", "mode", "plenumTemp", "returnTemp", "outsideTemp", "outsideHumid", "plenumHumid", "co2Level", "fanSpeed",],
                serviceMetrics
            },
            isCustom: false
        },
        {
            name: "Pile Health & Quality",
            description: "Focus on the crop itself with pile temperatures, humidity, and sugar levels.",
            icon: <AgricultureIcon />,
            metrics: {
                agristarMetrics: ["alarms", "mode", "pileTemp1", "pileTemp2", "pileTemp3", "plenumTemp", "deltaT", "co2Level", "plenumHumid",],
                serviceMetrics
            },
            isCustom: false
        },
    ];

    useEffect(() => {
        fetchPresets();
    }, []);

    const fetchPresets = async () => {
        const response = await API.getPresets();
        if (response && response.presets) {
            const formattedPresets = response.presets.map((p: any) => ({
                id: p.id,
                name: p.name,
                description: "Custom User Preset",
                icon: <StarIcon />,
                metrics: p.config,
                isCustom: true
            }));
            setCustomPresets(formattedPresets);
        }
    };

    const handlePresetSelect = (preset: Preset) => {
        setSelectedMetrics(preset.metrics);
        setSelectedPreset(preset.name);
    };

    const handleAddPreset = async () => {
        if (!newPresetName) return;

        if (editingPresetId) {
            const response = await API.updatePreset(editingPresetId, {
                name: newPresetName,
                config: dialogMetrics
            });
            if (response && response.preset) {
                fetchPresets();
                setIsDialogOpen(false);
                setNewPresetName("");
                setEditingPresetId(null);
            }
        } else {
            const response = await API.savePreset({
                name: newPresetName,
                config: dialogMetrics
            });

            if (response && response.preset) {
                fetchPresets();
                setIsDialogOpen(false);
                setNewPresetName("");
            }
        }
    };

    const handleEditPreset = () => {
        if (menuPresetId) {
            const preset = customPresets.find(p => p.id === menuPresetId);
            if (preset) {
                setNewPresetName(preset.name);
                setDialogMetrics(preset.metrics);
                setEditingPresetId(preset.id || null);
                setIsDialogOpen(true);
                handleMenuClose();
            }
        }
    };

    const handleOpenDialog = () => {
        if (selectedMetrics) {
            setDialogMetrics(selectedMetrics);
        }
        setIsDialogOpen(true);
    };

    const handleMetricToggle = (metricKey: string, isService: boolean) => {
        setDialogMetrics(prev => {
            const list = isService ? prev.serviceMetrics : prev.agristarMetrics;
            const newList = list.includes(metricKey)
                ? list.filter(m => m !== metricKey)
                : [...list, metricKey];
            
            return {
                ...prev,
                [isService ? 'serviceMetrics' : 'agristarMetrics']: newList
            };
        });
    };

    const handleDeletePreset = async () => {
        if (menuPresetId) {
            await API.deletePreset(menuPresetId);
            fetchPresets();
            handleMenuClose();
        }
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: number) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setMenuPresetId(id);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuPresetId(null);
    };

    const allPresets = [...defaultPresets, ...customPresets];

    return (
        <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
                {allPresets.map((preset) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={preset.name}>
                        <Card
                            variant="outlined"
                            sx={{
                                height: '100%',
                                position: 'relative',
                                transition: 'border-color 0.2s, background-color 0.2s',
                                ...(selectedPreset === preset.name && {
                                    borderColor: 'primary.main',
                                    borderWidth: 2,
                                    backgroundColor: 'action.hover'
                                })
                            }}
                        >
                            <CardActionArea onClick={() => handlePresetSelect(preset)} sx={{ height: '100%' }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                        <Box sx={{ mr: 1.5, color: 'primary.main', display: 'flex' }}>
                                            {preset.icon}
                                        </Box>
                                        <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
                                            {preset.name}
                                        </Typography>
                                        {preset.isCustom && (
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleMenuOpen(e, preset.id!)}
                                            >
                                                <MoreVertIcon />
                                            </IconButton>
                                        )}
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ pl: '44px' }}>
                                        {preset.description}
                                    </Typography>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Grid>
                ))}
                {customPresets.length < 3 && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Card variant="outlined" sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', borderStyle: 'dashed' }}>
                            <CardActionArea onClick={handleOpenDialog} sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
                                    <AddIcon color="action" sx={{ fontSize: 40, mb: 1 }} />
                                    <Typography color="text.secondary">Add Custom Preset</Typography>
                                </Box>
                            </CardActionArea>
                        </Card>
                    </Grid>
                )}
            </Grid>

<Dialog open={isDialogOpen} onClose={() => { setIsDialogOpen(false); setEditingPresetId(null); setNewPresetName(""); }} maxWidth="md" fullWidth>
                <DialogTitle>{editingPresetId ? "Edit Custom Preset" : "Save Custom Preset"}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        {editingPresetId 
                            ? "Update the preset name and select metrics." 
                            : "Create a new preset by selecting metrics."}
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Preset Name"
                        fullWidth
                        value={newPresetName}
                        onChange={(e) => setNewPresetName(e.target.value)}
                        sx={{ mb: 3 }}
                    />
                    
                    <Typography variant="h6" gutterBottom>Select Metrics</Typography>
                    
                    {Object.entries(DATA_CATEGORIES).map(([key, category]) => {
                        if (!category.agristarMetric && key !== 'service') return null;
                        
                        return (
                            <Accordion key={key} defaultExpanded={false}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography sx={{ fontWeight: 'bold', color: category.color }}>{category.label}</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container>
                                        {category.options.map((option) => (
                                            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={option.key}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={category.agristarMetric 
                                                                ? dialogMetrics.agristarMetrics.includes(option.key)
                                                                : dialogMetrics.serviceMetrics.includes(option.key)
                                                            }
                                                            onChange={() => handleMetricToggle(option.key, !category.agristarMetric)}
                                                            size="small"
                                                        />
                                                    }
                                                    label={<Typography variant="body2">{option.label}</Typography>}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}

                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setIsDialogOpen(false); setEditingPresetId(null); setNewPresetName(""); }}>Cancel</Button>
                    <Button 
                        onClick={handleAddPreset} 
                        variant="contained"
                        disabled={!newPresetName.trim() || (dialogMetrics.agristarMetrics.length === 0 && dialogMetrics.serviceMetrics.length === 0)}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={handleEditPreset}>Edit</MenuItem>
                <MenuItem onClick={handleDeletePreset} sx={{ color: 'error.main' }}>Delete</MenuItem>
            </Menu>
        </Box>
    );
}

export default PresetData;